// action ของ super_user — พอร์ตจาก Admin.gs (สิทธิ์ถูกบังคับที่ router)
//  รายการแอดมินอ่านข้อมูลล่าสุดสูงสุด 500 แถวต่อโหนด (orderBy createdAt) แล้วแบ่งหน้าในเครื่อง
import {
  fb, fk, err, str, num, bool, newId, dayKey, limitOf, parseJson, getRow, rowsOf, putRow, putNew, updateRow, readAllRaw,
  settings, pageByKey, pageRows, touchCache, clearAllCaches, DEFAULT_SETTINGS, CACHED_TABLES,
} from "./core";
import { getUser } from "./auth";
import { changeCoins, notify, vipState, vipPublic } from "./economy";
import { dropCards } from "./chars";
import { parseTiers } from "./pricing";

const CONFIG_COLS = {
  character_tags: "name,category,sort,isActive",
  packages: "name,icon,tagline,price,baseCoins,bonusCoins,bonusPercent,vipPoints,maxMessages,tag,sort,isActive",
  vip_tiers: "name,minPoints,monthlyCoins,checkinMultiplier,noteLimit,perks,sort",
  ai_models: "name,category,coinPerMessage,maxTokens,temperature,llmModel,description,styleHint,isNew,isDefault,isActive,sort",
  gifts: "name,nameTh,icon,coinCost,rewardType,rewardDuration,category,isActive,sort",
  promo_codes: "coins,maxUses,usedCount,perUser,expiresAt,isActive,note",
};
const CONFIG_TABLES = Object.keys(CONFIG_COLS);
const colsOf = (t) => CONFIG_COLS[t].split(",");

const adminLog = (ctx, action, targetId, detail) =>
  putNew("admin_log", "al", { actorId: ctx.uid, action, targetId: targetId || null, detail: typeof detail === "string" ? detail : JSON.stringify(detail || {}) });

async function pageTail(table, p, filterFn) {
  const rows = rowsOf(await fb.get(table, { orderBy: "createdAt", limitToLast: 500 }));
  return pageRows(rows, { limit: limitOf(p.limit, 30, 100), cursor: p.cursor, filter: filterFn, sort: "createdAt" });
}

export async function adminUsersList(p) {
  if (p.q) {
    const q = String(p.q).trim(), found = {};
    const add = (u) => {
      if (u) found[u.id] = u;
    };
    add(await getUser(q));
    rowsOf(await fb.get("users", { orderBy: "email", equalTo: q })).forEach(add);
    const byName = await fb.get(`usernames/${fk(q.toLowerCase())}`);
    if (byName) add(await getUser(byName));
    const byRef = await fb.get(`referral_codes/${fk(q.toUpperCase())}`);
    if (byRef) add(await getUser(byRef));
    return { items: Object.values(found).slice(0, 20), nextCursor: null };
  }
  return pageTail("users", p);
}

export async function adminUsersGet(p) {
  const u = await getUser(str(p.id, 100, "id", true));
  if (!u) throw err("not_found", "ไม่พบผู้ใช้");
  return { user: u, vip: vipPublic(await vipState(u)), coinTx: (await pageByKey(`coin_tx/${fk(u.id)}`, { limit: 30 })).items };
}

export async function adminUsersBan(p, ctx) {
  const id = str(p.id, 100, "id", true), banned = bool(p.banned);
  if (id === ctx.uid) throw err("bad_request", "แบนตัวเองไม่ได้");
  const reason = str(p.reason, 300, "เหตุผล", banned);
  const u = await updateRow(`users/${fk(id)}`, { banned, banReason: banned ? reason : null });
  await adminLog(ctx, banned ? "user.ban" : "user.unban", id, reason);
  return { user: u };
}

export async function adminAdjustCoins(p, ctx) {
  const id = str(p.id, 100, "id", true), amount = Math.trunc(num(p.amount));
  if (!amount) throw err("bad_request", "จำนวนคอยน์ต้องไม่เป็น 0");
  const reason = str(p.reason, 300, "เหตุผล", true);
  const r = await changeCoins(id, amount, { usageType: "admin_adjust", refType: "admin", note: reason, actorId: ctx.uid });
  await adminLog(ctx, "coins.adjust", id, { amount, reason });
  await notify(id, "system", amount > 0 ? "ได้รับคอยน์จากทีมงาน" : "ปรับยอดคอยน์โดยทีมงาน", (amount > 0 ? "+" : "") + amount + " คอยน์ — " + reason);
  return { balance: r.balance, tx: r.tx };
}

export async function adminCharactersList(p) {
  const q = String(p.q || "").toLowerCase();
  return pageTail("characters", p, (c) => (!p.status || c.status === p.status) && (!q || String(c.name).toLowerCase().indexOf(q) !== -1));
}

export async function adminCharactersReview(p, ctx) {
  const c = await getRow(`characters/${fk(str(p.id, 80, "id", true))}`);
  if (!c) throw err("not_found", "ไม่พบตัวละคร");
  const d = str(p.decision, 20, "decision", true), patch = {}, reason = str(p.reason, 300);
  let msg = "";
  if (d === "approve") { patch.status = "approved"; msg = `ตัวละคร "${c.name}" ได้รับการอนุมัติแล้ว`; }
  else if (d === "hide") { patch.status = "hidden"; patch.reviewNote = reason; msg = `ตัวละคร "${c.name}" ถูกซ่อนโดยทีมงาน ${reason}`; }
  else if (d === "reject") { patch.status = "rejected"; patch.reviewNote = reason; msg = `ตัวละคร "${c.name}" ไม่ผ่านการอนุมัติ ${reason}`; }
  else if (d === "feature") patch.isFeatured = true;
  else if (d === "unfeature") patch.isFeatured = false;
  else throw err("bad_request", "decision ไม่ถูกต้อง");
  const r = await updateRow(`characters/${fk(c.id)}`, patch);
  dropCards();
  await adminLog(ctx, "character." + d, c.id, reason);
  if (msg && c.createdBy && c.createdBy !== "system") await notify(c.createdBy, "character", "ผลการตรวจตัวละคร", msg, "/character/" + c.id);
  return { character: r };
}

function configTable(p) {
  const t = str(p.table, 40, "table", true);
  if (CONFIG_TABLES.indexOf(t) === -1) throw err("bad_request", "ตารางนี้แก้ผ่าน config ไม่ได้");
  return t;
}

export async function adminConfigList(p) {
  return { items: await readAllRaw(configTable(p)) };
}

export async function adminConfigSave(p, ctx) {
  const t = configTable(p), data = parseJson(p.data, null);
  if (!data || typeof data !== "object") throw err("bad_request", "ต้องระบุ data");
  const cols = colsOf(t), d = {};
  cols.forEach((c) => {
    if (data[c] !== undefined) d[c] = data[c];
  });
  let id = data.id ? String(data.id).trim() : "";
  if (t === "promo_codes") {
    id = id.toUpperCase();
    if (!/^[A-Z0-9_-]{3,40}$/.test(id)) throw err("bad_request", "โค้ดใช้ได้เฉพาะ A-Z 0-9 _ - (3-40 ตัว)");
    if (d.coins !== undefined && num(d.coins) <= 0) throw err("bad_request", "จำนวนคอยน์ต้องมากกว่า 0");
  }
  if (t === "packages" && d.price !== undefined && !(num(d.price) > 0)) throw err("bad_request", "ราคาต้องมากกว่า 0");
  if (t === "ai_models" && d.coinPerMessage !== undefined && num(d.coinPerMessage) < 0) throw err("bad_request", "ราคาต่อข้อความต้องไม่ติดลบ");
  if (!id) {
    if (t === "packages") id = newId("pk");
    else throw err("bad_request", "ต้องระบุ data.id");
  }
  if (!/^[\w\-.]{1,60}$/.test(id)) throw err("bad_request", "id ใช้ได้เฉพาะ a-z A-Z 0-9 _ - .");
  const path = `${t}/${fk(id)}`;
  let item;
  if (await getRow(path)) item = await updateRow(path, d);
  else {
    if (t === "promo_codes") d.usedCount = 0;
    if (d.isActive === undefined && cols.indexOf("isActive") !== -1) d.isActive = true;
    item = await putRow(path, t.slice(0, 2), { ...d, id });
  }
  if (t === "ai_models" && d.isDefault === true) {
    for (const m of await readAllRaw("ai_models")) {
      if (m.id !== id && m.isDefault === true) await updateRow(`ai_models/${fk(m.id)}`, { isDefault: false });
    }
  }
  touchCache(t);
  await adminLog(ctx, "config.save", t + ":" + id, d);
  return { item };
}

export async function adminConfigRemove(p, ctx) {
  const t = configTable(p), id = str(p.id, 60, "id", true);
  if (colsOf(t).indexOf("isActive") === -1) throw err("bad_request", "ตารางนี้ปิดการใช้งานไม่ได้ ให้แก้ค่าแทน");
  const item = await updateRow(`${t}/${fk(id)}`, { isActive: false });
  touchCache(t);
  await adminLog(ctx, "config.remove", t + ":" + id, "");
  return { item };
}

export const adminReportsList = (p) => pageTail("reports", p, (r) => !p.status || r.status === p.status);

export async function adminReportsResolve(p, ctx) {
  const r = await updateRow(`reports/${fk(str(p.id, 80, "id", true))}`, { status: "resolved", resolvedBy: ctx.uid, resolution: str(p.resolution, 500) });
  await adminLog(ctx, "report.resolve", r.id, r.resolution);
  return { report: r };
}

export const adminTopupsList = (p) => pageTail("topups", p, (t) => !p.status || t.status === p.status);

export async function adminStats(p) {
  const days = limitOf(p.days, 14, 60), keys = [], map = {};
  for (let i = days - 1; i >= 0; i--) {
    const k = dayKey(-i);
    keys.push(k);
    map[k] = { date: k, newUsers: 0, topupAmount: 0, topupCount: 0, coinsSpent: 0, messages: 0 };
  }
  const day = (iso) => (iso ? dayKey(0, new Date(iso).getTime()) : "");
  const since = new Date(Date.now() - (days + 1) * 86400000).toISOString();
  rowsOf(await fb.get("users", { orderBy: "createdAt", startAt: since })).forEach((u) => {
    const m = map[day(u.createdAt)];
    if (m) m.newUsers++;
  });
  rowsOf(await fb.get("topups", { orderBy: "createdAt", startAt: since })).forEach((t) => {
    const m = t.status === "credited" ? map[day(t.creditedAt || t.createdAt)] : null;
    if (m) {
      m.topupAmount += num(t.amount);
      m.topupCount++;
    }
  });
  const acts = await Promise.all(keys.map((k) => fb.get(`activity/${k}`)));
  keys.forEach((k, i) => {
    map[k].coinsSpent = num(acts[i] && acts[i].coinsSpent);
    map[k].messages = num(acts[i] && acts[i].messages);
  });
  const list = keys.map((k) => map[k]);
  const totals = { newUsers: 0, topupAmount: 0, topupCount: 0, coinsSpent: 0, messages: 0 };
  list.forEach((d) => {
    Object.keys(totals).forEach((f) => {
      totals[f] += d[f];
    });
  });
  return { days: list, totals };
}

export async function adminSettingsGet() {
  return { settings: await settings() };
}

export async function adminSettingsSet(p, ctx) {
  const key = str(p.key, 60, "key", true);
  if (!(key in DEFAULT_SETTINGS)) throw err("bad_request", "ไม่รู้จัก setting: " + key);
  const def = DEFAULT_SETTINGS[key];
  let v = p.value;
  if (typeof def === "number") {
    if (!isFinite(Number(v))) throw err("bad_request", "ต้องเป็นตัวเลข");
    v = Number(v);
  } else if (typeof def === "boolean") v = bool(v);
  else v = String(v === undefined || v === null ? "" : v).slice(0, 500);
  if (key === "coin_bonus_tiers" || key === "create_credit_tiers") {
    const raw = String(v).split(",").map((x) => x.trim()).filter(Boolean);
    if (parseTiers(v).length !== raw.length) throw err("bad_request", 'รูปแบบขั้นต้องเป็น "ขั้นต่ำ:เปอร์เซ็นต์" คั่นด้วยจุลภาค เช่น 500:10,1000:20 (เปอร์เซ็นต์ 0-100)');
  }
  if (["coin_per_baht", "custom_topup_min", "custom_topup_max", "create_credit_price", "create_credit_max"].indexOf(key) !== -1 && !(v > 0)) throw err("bad_request", "ต้องมากกว่า 0");
  const path = `settings/${fk(key)}`;
  if (await getRow(path)) await updateRow(path, { value: v });
  else await putRow(path, "st", { id: key, value: v });
  touchCache("settings");
  await adminLog(ctx, "setting.set", key, String(v));
  return { settings: await settings() };
}

export async function adminCacheClear() {
  CACHED_TABLES.forEach(touchCache);
  clearAllCaches();
  return { ok: true };
}
